// =============================================================================
// Fase 5.5 — Step 6: Dati di contatto + preferenza ricontatto
// Per contatto=chiamata → giorno + fascia; whatsapp/email → semplificato
// =============================================================================
import React from "react";
import { Phone, MessageCircle, Mail } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const CANALI = [
  { k: "chiamata", t: "Chiamata telefonica", d: "Ti chiamiamo nel giorno che preferisci", icon: Phone },
  { k: "whatsapp", t: "WhatsApp", d: "Ti contattiamo su WhatsApp", icon: MessageCircle },
  { k: "email", t: "Email", d: "Rispondiamo alla tua email", icon: Mail },
];

const FASCE = [
  ["mattina", "Mattina (9:00 – 12:30)"],
  ["pausa_pranzo", "Pausa pranzo (12:30 – 14:00)"],
  ["pomeriggio", "Pomeriggio (14:00 – 17:00)"],
  ["tardo_pomeriggio", "Tardo pomeriggio (17:00 – 19:30)"],
];

export function Step6Contatto({ state, setField, minimalContattoMode = false }) {
  // minimalContattoMode = true → percorso "contatto": no email/comune/note obbligatorie
  const canale = state.contatto_preferito;

  return (
    <div className="space-y-4" data-testid="rca-step6">
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-[#0F1F14]">
          {minimalContattoMode ? "I tuoi dati per essere contattato" : "Dati di contatto"}
        </h2>
        <p className="text-sm text-[#4A5E51] mt-1">
          {minimalContattoMode
            ? "Ci servono solo pochi dati per farti richiamare da un esperto."
            : "Ci servono per inviarti il preventivo e organizzare il contatto."}
        </p>
      </div>

      <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Nome *</Label>
            <Input
              value={state.contatto.nome}
              onChange={(e) => setField("contatto.nome", e.target.value)}
              data-testid="rca-contatto-nome"
            />
          </div>
          <div>
            <Label className="text-xs">Cognome *</Label>
            <Input
              value={state.contatto.cognome}
              onChange={(e) => setField("contatto.cognome", e.target.value)}
              data-testid="rca-contatto-cognome"
            />
          </div>
          <div>
            <Label className="text-xs">Telefono *</Label>
            <Input
              value={state.contatto.telefono}
              onChange={(e) => setField("contatto.telefono", e.target.value)}
              placeholder="333 1234567"
              data-testid="rca-contatto-telefono"
            />
          </div>
          {!minimalContattoMode && (
            <div>
              <Label className="text-xs">
                Email {canale === "email" ? "*" : "(consigliata)"}
              </Label>
              <Input
                type="email"
                value={state.contatto.email}
                onChange={(e) => setField("contatto.email", e.target.value)}
                data-testid="rca-contatto-email"
              />
            </div>
          )}
          {!minimalContattoMode && (
            <>
              <div>
                <Label className="text-xs">Codice fiscale contraente *</Label>
                <Input
                  value={state.contatto.codice_fiscale}
                  onChange={(e) =>
                    setField(
                      "contatto.codice_fiscale",
                      e.target.value.toUpperCase().replace(/\s/g, "")
                    )
                  }
                  maxLength={16}
                  placeholder="RSSMRA80A01H501Z"
                  data-testid="rca-contatto-cf"
                />
              </div>
              <div>
                <Label className="text-xs">Comune di residenza</Label>
                <Input
                  value={state.contatto.comune_residenza}
                  onChange={(e) => setField("contatto.comune_residenza", e.target.value)}
                  data-testid="rca-contatto-comune"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Provincia</Label>
                <Input
                  value={state.contatto.provincia_residenza}
                  onChange={(e) =>
                    setField(
                      "contatto.provincia_residenza",
                      e.target.value.toUpperCase().slice(0, 2)
                    )
                  }
                  maxLength={2}
                  placeholder="NA"
                  data-testid="rca-contatto-provincia"
                />
              </div>
            </>
          )}
        </div>

        {/* Note / messaggio */}
        <div>
          <Label className="text-xs">
            {minimalContattoMode ? "Messaggio (facoltativo)" : "Note per l'agenzia (facoltativo)"}
          </Label>
          <Textarea
            rows={3}
            maxLength={800}
            value={
              minimalContattoMode
                ? state.contatto_dettagli.messaggio
                : state.contatto.note
            }
            onChange={(e) =>
              minimalContattoMode
                ? setField("contatto_dettagli.messaggio", e.target.value)
                : setField("contatto.note", e.target.value)
            }
            data-testid="rca-contatto-note"
          />
        </div>
      </div>

      {/* Preferenza canale */}
      <div>
        <p className="text-sm font-medium text-[#0F1F14] mb-2">
          {minimalContattoMode
            ? "Come preferisci essere contattato?"
            : "Come preferisci essere ricontattato per questo preventivo?"}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {CANALI.filter((c) => !(minimalContattoMode && c.k === "email")).map((c) => {
            const on = canale === c.k;
            const Icon = c.icon;
            return (
              <button
                key={c.k}
                type="button"
                onClick={() => setField("contatto_preferito", c.k)}
                data-testid={`rca-canale-${c.k}`}
                className={`text-left rounded-xl border-2 p-3 transition-all ${
                  on
                    ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                    : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
                }`}
              >
                <Icon className="w-5 h-5 text-[var(--brand-accent,#163321)] mb-1.5" />
                <div className="text-sm font-semibold text-[#0F1F14]">{c.t}</div>
                <div className="text-[11px] text-[#4A5E51] mt-0.5">{c.d}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chiamata → giorno + fascia oraria */}
      {canale === "chiamata" && (
        <div
          className="rounded-2xl border border-[#E5E2DB] bg-[#FAF9F5] p-4 space-y-3"
          data-testid="rca-canale-chiamata-dettagli"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Giorno preferito *</Label>
              <Input
                type="date"
                value={state.contatto_dettagli.giorno}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setField("contatto_dettagli.giorno", e.target.value)}
                data-testid="rca-chiamata-giorno"
              />
            </div>
            <div>
              <Label className="text-xs">Fascia oraria *</Label>
              <Select
                value={state.contatto_dettagli.fascia_oraria}
                onValueChange={(v) => setField("contatto_dettagli.fascia_oraria", v)}
              >
                <SelectTrigger className="h-10 text-sm" data-testid="rca-chiamata-fascia">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent>
                  {FASCE.map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
